import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, CardContent, Checkbox, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Select, Stack, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip,
  Typography, Paper, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FlipIcon from '@mui/icons-material/Flip';
import './AdminDashboard.css';
import API_BASE_URL from '../../config';

const SEVERITY_OPTIONS = ['LOW', 'MEDIUM', 'HIGH', 'SIGHIGH'];

const DEFECT_TYPE_CODES = [
  'CLEANING',
  'COATING',
  'CORROSIO',
  'DAMAGE',
  'DISPLACE',
  'GRAFFITI',
  'INCORREC',
  'MISSING',
  'NA',
  'NONCOMP',
  'OLDLOGO'
];

const withDollar = (value) => {
  const amount = String(value ?? '').replace(/\$/g, '').trim();
  return amount ? `$${amount}` : '';
};

const SIGN_POLE_MATERIALS = [
  'Aluminium',
  'Glass',
  'Masonry',
  'Metal',
  'Plastic',
  'Stainless Steel',
  'Steel',
  'Steel Box',
  'Steel Galvanised',
  'Steel Powder Coated',
  'To Be Completed',
  'Timber'
];

const SIGN_POLE_TYPES = [
  'Warning',
  'Direction',
  'Electric',
  'Guide',
  'Information',
  'Naming',
  'Regulatory',
  'To Be Completed',
  'Traffic Control'
];

const ZONE_TYPES = [
  'Kangaroo',
  'Koala',
  'Not Applicable',
  'To Be Completed',
  'Wildlife'
];

const todayIsoDate = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

const SelectOrCustom = ({ label, value, onChange, options }) => {
  const match = options.find((option) => option.toLowerCase() === String(value || '').trim().toLowerCase());
  const [manual, setManual] = useState(() => Boolean(String(value || '').trim()) && !match);

  if (manual) {
    return (
      <TextField
        label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        fullWidth
        helperText={(
          <Button
            size="small"
            onClick={() => {
              setManual(false);
              if (!options.some((option) => option.toLowerCase() === String(value || '').trim().toLowerCase())) {
                onChange('');
              }
            }}
            sx={{ p: 0, minWidth: 0, textTransform: 'none', fontSize: 12 }}
          >
            Choose from list
          </Button>
        )}
      />
    );
  }

  return (
    <FormControl fullWidth>
      <InputLabel id={`${label}-select`}>{label}</InputLabel>
      <Select
        labelId={`${label}-select`}
        label={label}
        value={match || ''}
        onChange={(event) => {
          if (event.target.value === '__manual__') {
            setManual(true);
            onChange('');
            return;
          }
          onChange(event.target.value);
        }}
      >
        <MenuItem value="">Select…</MenuItem>
        {options.map((option) => (
          <MenuItem key={option} value={option}>{option}</MenuItem>
        ))}
        <MenuItem value="__manual__">Not listed — enter manually</MenuItem>
      </Select>
    </FormControl>
  );
};

const SCORE_COLORS = {
  1: { bg: '#e8f5e9', fg: '#1b5e20', border: '#a5d6a7' },
  2: { bg: '#f1f8e9', fg: '#33691e', border: '#c5e1a5' },
  3: { bg: '#fff8e1', fg: '#f57f17', border: '#ffe082' },
  4: { bg: '#fff3e0', fg: '#e65100', border: '#ffcc80' },
  5: { bg: '#ffebee', fg: '#b71c1c', border: '#ef9a9a' }
};

const SectionTitle = ({ step, children }) => (
  <Box
    sx={{
      mt: 3,
      mb: 1.75,
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      '&:first-of-type': { mt: 0.75 }
    }}
  >
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '8px',
        background: 'linear-gradient(145deg, #43a047 0%, #2e7d32 100%)',
        color: '#fff',
        fontSize: 13,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: '0 2px 6px rgba(46, 125, 50, 0.35)'
      }}
    >
      {step}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111', lineHeight: 1.2 }}>
        {children}
      </Typography>
      <Divider sx={{ mt: 0.75, borderColor: 'rgba(46, 125, 50, 0.18)' }} />
    </Box>
  </Box>
);

const TwoCol = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
      gap: 1.5
    }}
  >
    {children}
  </Box>
);

const formatAuDate = (value) => {
  if (value === undefined || value === null || String(value).trim() === '') return 'No date';
  const raw = String(value).trim();
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(raw)) return raw;
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    const dd = String(parsed.getDate()).padStart(2, '0');
    const mm = String(parsed.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${parsed.getFullYear()}`;
  }
  return raw;
};

const formatCoords = (lat, lon) => {
  const latitude = lat === undefined || lat === null || lat === '' ? null : Number(lat);
  const longitude = lon === undefined || lon === null || lon === '' ? null : Number(lon);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
};

const LocationCell = ({ device, onOpenMap }) => {
  const coords = formatCoords(device.latitude, device.longitude);
  if (!coords) {
    return <Typography variant="body2" sx={{ color: '#888' }}>No coordinates</Typography>;
  }
  return (
    <Tooltip title="View on map">
      <Button
        size="small"
        variant="text"
        onClick={() => onOpenMap(device)}
        sx={{
          color: '#1565c0',
          textTransform: 'none',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          px: 0,
          py: 0.25,
          minHeight: 32,
          width: '100%',
          maxWidth: '100%',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.35,
          whiteSpace: 'normal',
          textAlign: 'left',
          display: 'block'
        }}
      >
        {coords}
      </Button>
    </Tooltip>
  );
};

const ReportsList = ({ device, reports, onView, onDownload, selectedIds, onToggleSelect, onToggleStatus }) => {
  const items = reports || [];
  if (items.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: '#888', py: 0.5 }}>
        No reports yet
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        maxHeight: 220,
        overflowY: 'auto',
        border: '1px solid #d0d0d0',
        borderRadius: 1,
        backgroundColor: '#fff',
        minWidth: 0
      }}
    >
      {items.map((report, index) => {
        const finalised = report.status === 'finalised';
        return (
          <Box
            key={report.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 0.75,
              py: 0.5,
              borderBottom: index === items.length - 1 ? 'none' : '1px solid #ececec',
              minHeight: 40,
              flexWrap: 'wrap'
            }}
          >
            <Checkbox
              size="small"
              checked={selectedIds.has(report.id)}
              onChange={() => onToggleSelect(report.id)}
              inputProps={{ 'aria-label': `Select report ${formatAuDate(report.inspection_date)}` }}
              sx={{ p: 0.25 }}
            />
            <Box sx={{ flex: '1 1 120px', minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#222',
                  fontSize: 12,
                  lineHeight: 1.3,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word'
                }}
                title={`${formatAuDate(report.inspection_date)} · ${report.inspector_name || 'No inspector'}`}
              >
                {formatAuDate(report.inspection_date)}
                <Box component="span" sx={{ color: '#777' }}>
                  {' · '}{report.inspector_name || 'No inspector'}
                </Box>
              </Typography>
            </Box>
            <FormControlLabel
              sx={{ m: 0, flexShrink: 0 }}
              control={(
                <Switch
                  size="small"
                  checked={finalised}
                  onChange={() => onToggleStatus(report)}
                  color="success"
                />
              )}
              label={(
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: finalised ? '#1b5e20' : '#8a6d3b' }}>
                  {finalised ? 'Finalised' : 'Draft'}
                </Typography>
              )}
            />
            <Button size="small" onClick={() => onView(device, report.id)} sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: 11, flexShrink: 0 }}>
              View
            </Button>
            <Button size="small" onClick={() => onDownload(device, report.id, 'docx')} sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: 11, flexShrink: 0 }}>
              Word
            </Button>
            <Button size="small" onClick={() => onDownload(device, report.id, 'pdf')} sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: 11, flexShrink: 0 }}>
              PDF
            </Button>
          </Box>
        );
      })}
    </Box>
  );
};

const normalizeSeverity = (value) => {
  const match = SEVERITY_OPTIONS.find((option) => option.toLowerCase() === String(value || '').trim().toLowerCase());
  return match || '';
};

const COMPONENT_SECTIONS = [
  {
    title: 'Foundation',
    items: [
      { name: 'Foundation (footing)', label: 'Footing' }
    ]
  },
  {
    title: 'Structure',
    items: [
      { name: 'Structure - Pole', label: 'Pole' },
      { name: 'Structure – Battery Enclosure', label: 'Battery enclosure' },
      { name: 'Structure – Cabinet', label: 'Cabinet' },
      { name: 'Structure - Fixings', label: 'Fixings' },
      { name: 'Structure – Locks & Fittings', label: 'Locks & fittings' },
      { name: 'Structure – Labelling & Signage', label: 'Labelling & signage' }
    ]
  },
  {
    title: 'Faceplate',
    items: [
      { name: 'Faceplate – Enclosure', label: 'Enclosure' },
      { name: 'Faceplate – LED lights', label: 'LED lights' }
    ]
  },
  {
    title: 'Electrical supply',
    items: [
      { name: 'Electrical Supply – PV panel', label: 'PV panel' },
      { name: 'Electrical Supply – Wiring', label: 'Wiring' },
      { name: 'Electrical Supply – Battery', label: 'Battery' },
      { name: 'Electrical Supply – Control System', label: 'Control system' }
    ]
  }
];

const COMPONENTS = COMPONENT_SECTIONS.flatMap((section) => section.items.map((item) => item.name));

const SCORE_GUIDE = [
  {
    score: '1',
    rating: 'Very good (as new)',
    life: '90% – 100%',
    description: 'Free of defects with little or no deterioration evident, (e.g., as new condition, clean free of debris, no corrosion, not covered with vegetation, functioning properly)'
  },
  {
    score: '2',
    rating: 'Good',
    life: '56% – 90%',
    description: 'Deterioration of a minor nature, (e.g. functioning properly, minor cracks)'
  },
  {
    score: '3',
    rating: 'Fair',
    life: '25% – 56%',
    description: 'Moderate to significant deterioration, (e.g., functioning properly, visible cracks, signs of corrosion)'
  },
  {
    score: '4',
    rating: 'Poor',
    life: '6% – 25%',
    description: 'Significant deterioration and defects. (e.g., frequent breakdown, service issues, nearing to fail)'
  },
  {
    score: '5',
    rating: 'Very Poor',
    life: '0% – 6%',
    description: 'Failed or failure imminent, (e.g. completely broken, not operational)'
  }
];

const emptyDefect = () => ({
  inspectorName: '',
  inspectionDate: todayIsoDate(),
  defectNumber: '',
  area: '',
  typeCode: '',
  severity: '',
  details: '',
  recommendation: '',
  cost: '',
  photos: []
});

const fileToJpegDataUrl = (file) => new Promise((resolve, reject) => {
  const image = new Image();
  const url = URL.createObjectURL(file);
  image.onload = () => {
    const scale = Math.min(1, 1600 / Math.max(image.width, image.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));
    canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    resolve(canvas.toDataURL('image/jpeg', 0.8));
  };
  image.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error('Could not read the photo'));
  };
  image.src = url;
});

const PhotoPicker = ({ touch, buttonLabel, helper, photos, onAdd, onRemove }) => {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [flipX, setFlipX] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const thumbSize = isMobile ? 112 : 180;

  const openPreview = (photo) => {
    setPreview(photo);
    setZoom(1);
    setRotation(0);
    setFlipX(false);
  };

  const closePreview = () => {
    setPreview(null);
    setZoom(1);
    setRotation(0);
    setFlipX(false);
  };

  return (
    <Box>
      <Button
        variant="outlined"
        fullWidth={isMobile}
        onClick={() => inputRef.current?.click()}
        sx={{ color: '#111', borderColor: '#4CAF50', minHeight: 48, fontSize: isMobile ? 16 : undefined }}
      >
        {buttonLabel}
      </Button>
      <input
        ref={inputRef}
        hidden
        type="file"
        accept="image/*"
        capture={touch ? 'environment' : undefined}
        multiple={!touch}
        onChange={async (event) => {
          const files = Array.from(event.target.files || []);
          event.target.value = '';
          const dataUrls = [];
          for (const file of files) {
            dataUrls.push(await fileToJpegDataUrl(file));
          }
          if (dataUrls.length) onAdd(dataUrls);
        }}
      />
      <Typography variant="caption" display="block" sx={{ mt: 1, color: '#333' }}>{helper}</Typography>
      <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1, flexWrap: 'wrap' }}>
        {(photos || []).filter((photo) => photo.dataUrl).map((photo, index) => (
          <Box key={`${photo.name}-${index}`} sx={{ width: thumbSize }}>
            <img
              src={photo.dataUrl}
              alt={photo.name}
              onClick={() => openPreview(photo)}
              style={{
                width: thumbSize,
                height: Math.round(thumbSize * 0.78),
                objectFit: 'cover',
                display: 'block',
                cursor: 'pointer',
                border: '1px solid #ccc',
                borderRadius: 4
              }}
            />
            <Typography variant="caption" display="block" sx={{ wordBreak: 'break-all' }}>{photo.name}</Typography>
            <Button size="small" onClick={() => onRemove(index)} sx={{ minHeight: 40 }}>Remove</Button>
          </Box>
        ))}
      </Stack>
      <Dialog open={Boolean(preview)} onClose={closePreview} fullScreen={isMobile} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ color: '#111', display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', pr: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{preview?.name}</Box>
          <IconButton aria-label="Zoom out" onClick={() => setZoom((value) => Math.max(0.5, Number((value - 0.25).toFixed(2))))}>
            <ZoomOutIcon />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: 48, textAlign: 'center' }}>{Math.round(zoom * 100)}%</Typography>
          <IconButton aria-label="Zoom in" onClick={() => setZoom((value) => Math.min(4, Number((value + 0.25).toFixed(2))))}>
            <ZoomInIcon />
          </IconButton>
          <IconButton aria-label="Rotate left" onClick={() => setRotation((value) => value - 90)}>
            <RotateLeftIcon />
          </IconButton>
          <IconButton aria-label="Rotate right" onClick={() => setRotation((value) => value + 90)}>
            <RotateRightIcon />
          </IconButton>
          <IconButton aria-label="Flip horizontal" onClick={() => setFlipX((value) => !value)}>
            <FlipIcon />
          </IconButton>
          <IconButton aria-label="Reset view" onClick={() => { setZoom(1); setRotation(0); setFlipX(false); }}>
            <RestartAltIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{ backgroundColor: '#111', minHeight: isMobile ? '50vh' : '60vh', overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          onWheel={(event) => {
            event.preventDefault();
            const delta = event.deltaY > 0 ? -0.1 : 0.1;
            setZoom((value) => Math.min(4, Math.max(0.5, Number((value + delta).toFixed(2)))));
          }}
        >
          {preview && (
            <img
              src={preview.dataUrl}
              alt={preview.name}
              draggable={false}
              style={{
                maxWidth: '100%',
                maxHeight: isMobile ? '65vh' : '70vh',
                transform: `scale(${zoom}) rotate(${rotation}deg) scaleX(${flipX ? -1 : 1})`,
                transformOrigin: 'center center',
                transition: 'transform 0.15s ease',
                userSelect: 'none'
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ pb: isMobile ? 2 : 1 }}>
          <Button fullWidth={isMobile} onClick={closePreview} sx={{ minHeight: 48 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const blankReport = (device) => ({
  assetNumber: device?.device_name || '',
  assetDescription: device?.description || '',
  assetLocation: [device?.street_address, device?.suburb].filter(Boolean).join(', '),
  inspectorName: '',
  inspectionDate: todayIsoDate(),
  make: '',
  model: '',
  serialNumber: '',
  manufactureYear: '',
  usefulLife: '',
  remainingUsefulLife: '',
  estimatedReplacementCost: '',
  assetPhotographs: '',
  signPoleMaterial: '',
  signPoleHeight: '',
  signPoleDiameter: '',
  signPoleType: 'Warning',
  mutcdSignCode: 'W5-29',
  signText: '',
  zoneType: '',
  batteryMakeModel: '',
  batteryCapacity: '',
  pvCellCount: '',
  pvCapacity: '',
  gridConnect: 'No',
  assetPhotos: [],
  components: COMPONENTS.map((name) => ({ name, conditionScore: '', comments: '' })),
  defects: []
});

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

const AdminInspection = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const useCardLayout = useMediaQuery(theme.breakpoints.down('lg'));
  const [devices, setDevices] = useState([]);
  const [selectedUser, setSelectedUser] = useState('all');
  const [searchName, setSearchName] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeDevice, setActiveDevice] = useState(null);
  const [form, setForm] = useState(blankReport());
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [touchCamera, setTouchCamera] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(pointer: coarse)');
    const update = () => setTouchCamera(media.matches || navigator.maxTouchPoints > 0);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);

  const fetchDevices = async () => {
    const response = await fetch(`${API_BASE_URL}/admin/inspection-devices`, {
      headers: authHeaders()
    });
    if (response.status === 401) {
      navigate('/unauthorized');
      return;
    }
    const data = await response.json();
    setDevices(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    fetchDevices().catch((error) => {
      console.error(error);
      setMessage('Failed to load inspection devices');
    });
  }, []);

  const customers = useMemo(() => {
    const names = new Map();
    devices.forEach((device) => {
      if (device.user_id) names.set(device.user_id, device.customer || 'Unknown customer');
    });
    return Array.from(names.entries());
  }, [devices]);

  const visibleDevices = devices.filter((device) => {
    const userMatch = selectedUser === 'all' || device.user_id === selectedUser;
    if (!searchName) return userMatch;

    const query = searchName.toLowerCase().trim();
    const deviceFields = [
      device.customer,
      device.device_name,
      device.description,
      device.street_address,
      device.suburb,
      device.longitude,
      device.latitude,
      device.created_at
    ];
    const reportFields = (device.reports || []).flatMap((report) => [
      report.inspector_name,
      report.inspection_date,
      report.updated_at,
      report.created_at
    ]);
    const haystack = [...deviceFields, ...reportFields]
      .filter((value) => value !== undefined && value !== null && String(value).trim() !== '')
      .map((value) => String(value).toLowerCase())
      .join(' ');

    return userMatch && haystack.includes(query);
  });

  const visibleReportIds = visibleDevices.flatMap((device) => (device.reports || []).map((report) => report.id));
  const allVisibleSelected = visibleReportIds.length > 0 && visibleReportIds.every((id) => selectedIds.has(id));
  const canSave = String(form.inspectorName || '').trim() !== '' && String(form.inspectionDate || '').trim() !== '';

  const toggleSelect = (reportId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(reportId)) next.delete(reportId);
      else next.add(reportId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        visibleReportIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...visibleReportIds]);
    });
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const openOnAdminMap = (device) => {
    const params = new URLSearchParams({
      device: device.device_name || '',
      kind: 'inspection'
    });
    if (device.latitude != null && device.longitude != null) {
      params.set('lat', String(device.latitude));
      params.set('lon', String(device.longitude));
    }
    navigate(`/admin/maps?${params.toString()}`);
  };

  const openCreate = (device) => {
    setActiveDevice(device);
    setEditingId(null);
    setForm(blankReport(device));
    setMessage('');
    setDialogOpen(true);
  };

  const openEdit = async (device, reportId) => {
    setActiveDevice(device);
    setMessage('');
    const response = await fetch(`${API_BASE_URL}/admin/inspection-reports/${reportId}`, {
      headers: authHeaders()
    });
    const data = await response.json();
    if (!response.ok) {
      setMessage(data.message || 'Failed to open report');
      return;
    }
    const saved = data.report_data || {};
    setEditingId(reportId);
    setForm({
      ...blankReport(device),
      ...saved,
      estimatedReplacementCost: withDollar(saved.estimatedReplacementCost),
      assetNumber: device.device_name,
      assetPhotos: Array.isArray(saved.assetPhotos) ? saved.assetPhotos : [],
      components: COMPONENTS.map((name) => {
        const found = (saved.components || []).find((item) => item.name === name) || {};
        return { name, conditionScore: found.conditionScore || '', comments: found.comments || '' };
      }),
      defects: (saved.defects || []).map((defect, index) => ({
        ...emptyDefect(),
        ...defect,
        defectNumber: defect.defectNumber || `${device.device_name}_${String(index + 1).padStart(3, '0')}`,
        severity: normalizeSeverity(defect.severity),
        cost: withDollar(defect.cost),
        photos: Array.isArray(defect.photos) ? defect.photos : []
      }))
    });
    setDialogOpen(true);
  };

  const saveReport = async () => {
    setSaving(true);
    setMessage('');
    try {
      const response = await fetch(
        editingId
          ? `${API_BASE_URL}/admin/inspection-reports/${editingId}`
          : `${API_BASE_URL}/admin/inspection-reports`,
        {
          method: editingId ? 'PUT' : 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            ...form,
            inspectionDeviceId: activeDevice.id
          })
        }
      );
      const data = await response.json();
      if (!response.ok || data.success === false) {
        setMessage(data.message || 'Failed to save report');
        return;
      }
      setDialogOpen(false);
      setMessage(editingId ? 'Inspection report updated' : 'Inspection report saved');
      await fetchDevices();
    } catch (error) {
      console.error(error);
      setMessage('Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const downloadReport = async (device, reportId, format) => {
    const response = await fetch(`${API_BASE_URL}/admin/inspection-reports/${reportId}/download?format=${format}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });
    if (!response.ok) {
      setMessage('Failed to download report');
      return;
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${device.device_name}_Condition_Inspection.${format}`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const toggleReportStatus = async (report) => {
    const nextStatus = report.status === 'finalised' ? 'draft' : 'finalised';
    setMessage('');
    const response = await fetch(`${API_BASE_URL}/admin/inspection-reports/${report.id}/status`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ status: nextStatus })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) {
      setMessage(data.message || 'Failed to update report status');
      return;
    }
    setMessage(nextStatus === 'finalised' ? 'Report finalised and visible to the customer' : 'Report set back to draft');
    await fetchDevices();
  };

  const bulkDownload = async (kind) => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setBulkBusy(true);
    setMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/admin/inspection-reports/bulk-download`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ids, kind })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setMessage(data.message || 'Failed to download selection');
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = kind === 'photos'
        ? 'inspection-photos.zip'
        : `inspection-reports-${kind}.zip`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      setMessage('Failed to download selection');
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="admin-dashboard" style={{ paddingTop: isMobile ? 72 : 88, paddingLeft: isMobile ? 8 : undefined, paddingRight: isMobile ? 8 : undefined }}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', lg: 'center' }}
        sx={{ mb: 2, px: { xs: 0.5, md: 1 } }}
      >
        <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ flexGrow: 1 }}>Inspection</Typography>
        <TextField
          size={isMobile ? 'medium' : 'small'}
          label="Search"
          placeholder="Customer, device, address..."
          value={searchName}
          onChange={(event) => setSearchName(event.target.value)}
          fullWidth={useCardLayout}
          sx={{ minWidth: { lg: 240 }, maxWidth: { lg: 320 }, backgroundColor: '#fff' }}
        />
        <FormControl size={isMobile ? 'medium' : 'small'} fullWidth={useCardLayout} sx={{ minWidth: { lg: 200 }, maxWidth: { lg: 260 } }}>
          <InputLabel id="inspection-user-label">Customer</InputLabel>
          <Select
            labelId="inspection-user-label"
            label="Customer"
            value={selectedUser}
            onChange={(event) => setSelectedUser(event.target.value)}
          >
            <MenuItem value="all">All devices</MenuItem>
            {customers.map(([id, name]) => (
              <MenuItem key={id} value={id}>{name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      {message && (
        <Typography sx={{ px: 1, mb: 1, color: /fail|no photo|not found|select at least/i.test(message) ? '#c62828' : '#2e7d32' }}>
          {message}
        </Typography>
      )}

      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ mb: 1.5, px: { xs: 0.5, md: 1 }, flexWrap: 'wrap', alignItems: 'center' }}
      >
        <Button
          variant="outlined"
          onClick={toggleSelectAll}
          disabled={!visibleReportIds.length}
          sx={{ textTransform: 'none', minHeight: 40, borderColor: '#2e7d32', color: '#1b5e20' }}
        >
          {allVisibleSelected ? 'Unselect all' : 'Select all'}
        </Button>
        <Button
          variant="contained"
          disabled={!selectedIds.size || bulkBusy}
          onClick={() => bulkDownload('photos')}
          sx={{ textTransform: 'none', minHeight: 40, backgroundColor: '#2e7d32' }}
        >
          Download photos
        </Button>
        <Button
          variant="contained"
          disabled={!selectedIds.size || bulkBusy}
          onClick={() => bulkDownload('docx')}
          sx={{ textTransform: 'none', minHeight: 40, backgroundColor: '#2e7d32' }}
        >
          Download Word
        </Button>
        <Button
          variant="contained"
          disabled={!selectedIds.size || bulkBusy}
          onClick={() => bulkDownload('pdf')}
          sx={{ textTransform: 'none', minHeight: 40, backgroundColor: '#2e7d32' }}
        >
          Download PDF
        </Button>
        <Typography variant="body2" sx={{ color: '#555' }}>
          {selectedIds.size ? `${selectedIds.size} selected` : 'Select reports to download'}
        </Typography>
      </Stack>

      {useCardLayout ? (
        <Stack spacing={1.5} sx={{ pb: 3 }}>
          {visibleDevices.map((device) => (
            <Card key={device.id} sx={{ backgroundColor: '#f5f5f5', borderRadius: 2 }}>
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111', mb: 0.25 }}>
                      {device.device_name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#333' }}>{device.customer || '—'}</Typography>
                    <Typography variant="body2" sx={{ color: '#555' }}>
                      {[device.description, device.street_address, device.suburb].filter(Boolean).join(' · ') || 'No location'}
                    </Typography>
                    <Box sx={{ mt: 0.75 }}>
                      <LocationCell
                        device={device}
                        onOpenMap={openOnAdminMap}
                      />
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<AssignmentIcon />}
                    onClick={() => openCreate(device)}
                    sx={{
                      backgroundColor: 'rgba(76, 175, 80, 0.9)',
                      minHeight: 44,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      alignSelf: { xs: 'stretch', sm: 'flex-start' }
                    }}
                  >
                    Create Report
                  </Button>
                </Stack>
                <ReportsList
                  device={device}
                  reports={device.reports}
                  onView={openEdit}
                  onDownload={downloadReport}
                  selectedIds={selectedIds}
                  onToggleSelect={toggleSelect}
                  onToggleStatus={toggleReportStatus}
                />
              </CardContent>
            </Card>
          ))}
          {visibleDevices.length === 0 && (
            <Paper sx={{ p: 2, backgroundColor: '#eee' }}>
              <Typography>No inspection devices for this selection.</Typography>
            </Paper>
          )}
        </Stack>
      ) : (
        <TableContainer component={Paper} sx={{ backgroundColor: '#cacaca', overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 1280, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 140 }}>Customer</TableCell>
                <TableCell sx={{ width: 120 }}>Device ID</TableCell>
                <TableCell sx={{ width: 160 }}>Description</TableCell>
                <TableCell sx={{ width: 220 }}>Location</TableCell>
                <TableCell sx={{ width: 170 }}>Coordinates</TableCell>
                <TableCell sx={{ width: 460 }}>Reports</TableCell>
                <TableCell align="right" sx={{ width: 110 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleDevices.map((device, index) => (
                <TableRow key={device.id} sx={{ backgroundColor: index % 2 ? '#e6e6e6' : '#f5f5f5', verticalAlign: 'top' }}>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{device.customer || ''}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{device.device_name}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{device.description}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>
                    {[device.street_address, device.suburb].filter(Boolean).join(', ') || '—'}
                  </TableCell>
                  <TableCell>
                    <LocationCell
                      device={device}
                      onOpenMap={openOnAdminMap}
                    />
                  </TableCell>
                  <TableCell>
                    <ReportsList
                      device={device}
                      reports={device.reports}
                      onView={openEdit}
                      onDownload={downloadReport}
                      selectedIds={selectedIds}
                      onToggleSelect={toggleSelect}
                      onToggleStatus={toggleReportStatus}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      variant="contained"
                      onClick={() => openCreate(device)}
                      sx={{
                        backgroundColor: 'rgba(76, 175, 80, 0.9)',
                        whiteSpace: 'nowrap',
                        minWidth: 88,
                        px: 1.5
                      }}
                    >
                      Create
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {visibleDevices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>No inspection devices for this selection.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullScreen={isMobile}
        maxWidth="md"
        fullWidth
        scroll="paper"
        BackdropProps={{
          sx: { backgroundColor: 'rgba(15, 23, 15, 0.45)', backdropFilter: 'blur(2px)' }
        }}
        PaperProps={{
          sx: {
            backgroundColor: '#fafbfa',
            color: '#111',
            height: { xs: '100%', sm: 'min(92vh, 920px)' },
            m: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: '16px' },
            overflow: 'hidden',
            boxShadow: {
              xs: 'none',
              sm: '0 24px 64px rgba(20, 40, 20, 0.22), 0 0 0 1px rgba(46, 125, 50, 0.08)'
            }
          }
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 55%, #43a047 100%)',
            color: '#fff',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: 3,
              background: 'linear-gradient(90deg, #a5d6a7, #fff59d, #a5d6a7)'
            }
          }}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{ color: 'rgba(255,255,255,0.75)', letterSpacing: 1.2, lineHeight: 1, display: 'block', mb: 0.5 }}
            >
              Condition inspection
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, fontSize: { xs: '1.15rem', sm: '1.35rem' }, color: '#fff' }}>
              {editingId ? 'Edit Report' : 'Create Report'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.88)', mt: 0.4, fontWeight: 500 }}>
              {activeDevice?.device_name || 'Device'}
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={() => setDialogOpen(false)}
            size="small"
            sx={{
              mt: -0.25,
              color: '#fff',
              backgroundColor: 'rgba(255,255,255,0.12)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.22)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            background: 'linear-gradient(180deg, #f7faf7 0%, #ffffff 120px)',
            color: '#111',
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2.5 },
            borderColor: 'rgba(46, 125, 50, 0.12)'
          }}
        >
          <SectionTitle step={1}>Asset details</SectionTitle>
          <Stack spacing={1.5}>
            <TwoCol>
              <TextField label="Asset number" value={form.assetNumber} disabled fullWidth />
              <TextField
                label="Inspector's name"
                value={form.inspectorName}
                onChange={(event) => setField('inspectorName', event.target.value)}
                required
                fullWidth
              />
            </TwoCol>
            <TextField
              label="Asset description"
              value={form.assetDescription}
              onChange={(event) => setField('assetDescription', event.target.value)}
              fullWidth
            />
            <TwoCol>
              <TextField
                label="Asset location"
                value={form.assetLocation}
                onChange={(event) => setField('assetLocation', event.target.value)}
                helperText="Prefilled from street address and suburb"
                fullWidth
              />
              <TextField
                label="Inspection date"
                type="date"
                value={form.inspectionDate}
                onChange={(event) => setField('inspectionDate', event.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
              />
            </TwoCol>
            <TwoCol>
              <TextField label="Make" value={form.make} onChange={(event) => setField('make', event.target.value)} fullWidth />
              <TextField label="Model" value={form.model} onChange={(event) => setField('model', event.target.value)} fullWidth />
            </TwoCol>
            <TwoCol>
              <TextField label="Serial number" value={form.serialNumber} onChange={(event) => setField('serialNumber', event.target.value)} fullWidth />
              <TextField label="Manufacture year" value={form.manufactureYear} onChange={(event) => setField('manufactureYear', event.target.value)} fullWidth />
            </TwoCol>
            <TwoCol>
              <TextField label="Useful life" value={form.usefulLife} onChange={(event) => setField('usefulLife', event.target.value)} fullWidth />
              <TextField label="Remaining useful life" value={form.remainingUsefulLife} onChange={(event) => setField('remainingUsefulLife', event.target.value)} fullWidth />
            </TwoCol>
            <TextField
              label="Estimated replacement cost"
              value={form.estimatedReplacementCost}
              onChange={(event) => setField('estimatedReplacementCost', withDollar(event.target.value))}
              fullWidth
            />
            <PhotoPicker
              touch={touchCamera}
              buttonLabel={touchCamera ? 'Take asset photo' : 'Upload asset photos'}
              helper={`Photos saved as JPG — ${form.assetNumber || 'assetNumber'}_#.jpg`}
              photos={form.assetPhotos}
              onAdd={(dataUrls) => setForm((prev) => {
                const photos = [...prev.assetPhotos, ...dataUrls.map((dataUrl) => ({ dataUrl }))];
                return {
                  ...prev,
                  assetPhotos: photos.map((photo, index) => ({ ...photo, name: `${prev.assetNumber}_${index + 1}.jpg` }))
                };
              })}
              onRemove={(photoIndex) => setForm((prev) => ({
                ...prev,
                assetPhotos: prev.assetPhotos
                  .filter((_, index) => index !== photoIndex)
                  .map((photo, index) => ({ ...photo, name: `${prev.assetNumber}_${index + 1}.jpg` }))
              }))}
            />

            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: '#1b5e20',
                pt: 1,
                pb: 0.25,
                borderLeft: '3px solid #66bb6a',
                pl: 1.25
              }}
            >
              Sign pole & signage
            </Typography>
            <SelectOrCustom
              key={`material-${dialogOpen}-${editingId || 'new'}`}
              label="Sign pole material"
              value={form.signPoleMaterial}
              options={SIGN_POLE_MATERIALS}
              onChange={(next) => setField('signPoleMaterial', next)}
            />
            <TwoCol>
              <TextField
                label="Sign pole height (m)"
                value={form.signPoleHeight}
                onChange={(event) => setField('signPoleHeight', event.target.value)}
                fullWidth
              />
              <TextField
                label="Sign pole diameter (mm)"
                value={form.signPoleDiameter}
                onChange={(event) => setField('signPoleDiameter', event.target.value)}
                fullWidth
              />
            </TwoCol>
            <TwoCol>
              <SelectOrCustom
                key={`pole-type-${dialogOpen}-${editingId || 'new'}`}
                label="Sign pole type"
                value={form.signPoleType}
                options={SIGN_POLE_TYPES}
                onChange={(next) => setField('signPoleType', next)}
              />
              <TextField
                label="MUTCD sign code"
                value={form.mutcdSignCode}
                onChange={(event) => setField('mutcdSignCode', event.target.value)}
                helperText="TMR MUTCD Q-series / TC signs"
                fullWidth
              />
            </TwoCol>
            <TwoCol>
              <TextField label="Sign text" value={form.signText} onChange={(event) => setField('signText', event.target.value)} fullWidth />
              <SelectOrCustom
                key={`zone-${dialogOpen}-${editingId || 'new'}`}
                label="Zone type"
                value={form.zoneType}
                options={ZONE_TYPES}
                onChange={(next) => setField('zoneType', next)}
              />
            </TwoCol>

            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: '#1b5e20',
                pt: 1,
                pb: 0.25,
                borderLeft: '3px solid #66bb6a',
                pl: 1.25
              }}
            >
              Power & battery
            </Typography>
            <TwoCol>
              <TextField label="Battery make and model" value={form.batteryMakeModel} onChange={(event) => setField('batteryMakeModel', event.target.value)} fullWidth />
              <TextField label="Battery capacity" value={form.batteryCapacity} onChange={(event) => setField('batteryCapacity', event.target.value)} fullWidth />
            </TwoCol>
            <TwoCol>
              <TextField label="PV panel no. of cells" value={form.pvCellCount} onChange={(event) => setField('pvCellCount', event.target.value)} fullWidth />
              <TextField
                label="PV panel capacity (W)"
                value={form.pvCapacity}
                onChange={(event) => setField('pvCapacity', event.target.value)}
                fullWidth
              />
            </TwoCol>
            <FormControl fullWidth>
              <InputLabel id="grid-connect-label">Grid-connect supply</InputLabel>
              <Select
                labelId="grid-connect-label"
                label="Grid-connect supply"
                value={form.gridConnect}
                onChange={(event) => setField('gridConnect', event.target.value)}
              >
                <MenuItem value="">Select…</MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <SectionTitle step={2}>Component scoring</SectionTitle>
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              mb: 2.5,
              border: '1px solid rgba(46, 125, 50, 0.2)',
              borderRadius: '12px !important',
              backgroundColor: '#fff',
              boxShadow: '0 4px 18px rgba(20, 40, 20, 0.06)',
              overflow: 'hidden',
              '&:before': { display: 'none' }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 2,
                background: 'linear-gradient(90deg, rgba(232,245,233,0.9), #fff)',
                '& .MuiAccordionSummary-content': { my: 1.25 }
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1b5e20' }}>
                  Score guide (Appendix E)
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Condition score, rating, remaining useful life, and description
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: { xs: 1, sm: 2 }, pb: 2 }}>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: { xs: 520, sm: 640 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, width: 64 }}>CS</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 140 }}>Subjective rating</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 110 }}>Remaining life</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {SCORE_GUIDE.map((row) => {
                      const colors = SCORE_COLORS[row.score] || SCORE_COLORS[3];
                      return (
                        <TableRow
                          key={row.score}
                          sx={{
                            '&:nth-of-type(odd)': { backgroundColor: 'rgba(0,0,0,0.015)' },
                            verticalAlign: 'top'
                          }}
                        >
                          <TableCell>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 800,
                                fontSize: 14,
                                color: colors.fg,
                                backgroundColor: colors.bg,
                                border: `1px solid ${colors.border}`
                              }}
                            >
                              {row.score}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#222' }}>{row.rating}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', color: '#444' }}>{row.life}</TableCell>
                          <TableCell sx={{ color: '#444', fontSize: 13, lineHeight: 1.45 }}>
                            {row.description}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          <Stack spacing={1.5}>
            {COMPONENT_SECTIONS.map((section) => (
              <Box
                key={section.title}
                sx={{
                  border: '1px solid rgba(46, 125, 50, 0.14)',
                  borderRadius: '12px',
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 18px rgba(20, 40, 20, 0.05)',
                  px: { xs: 1.25, sm: 2 },
                  py: 1.5
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: '#1b5e20',
                    mb: 1.25,
                    borderLeft: '3px solid #66bb6a',
                    pl: 1.25
                  }}
                >
                  {section.title}
                </Typography>
                <Stack spacing={1.5}>
                  {section.items.map((item) => {
                    const index = form.components.findIndex((component) => component.name === item.name);
                    const component = index >= 0 ? form.components[index] : { name: item.name, conditionScore: '', comments: '' };
                    return (
                      <Box
                        key={item.name}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'minmax(150px, 190px) 96px minmax(0, 1fr)' },
                          gap: 1.25,
                          alignItems: 'start',
                          pb: 1.25,
                          borderBottom: '1px solid #f0f0f0',
                          '&:last-of-type': { pb: 0, borderBottom: 'none' }
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: '#222', pt: { sm: 1 }, whiteSpace: 'normal', wordBreak: 'break-word' }}
                        >
                          {item.label}
                        </Typography>
                        <FormControl fullWidth size="small">
                          <InputLabel id={`score-${index}`}>Score</InputLabel>
                          <Select
                            labelId={`score-${index}`}
                            label="Score"
                            value={component.conditionScore}
                            renderValue={(selected) => (selected ? selected : '—')}
                            onChange={(event) => setForm((prev) => {
                              const components = [...prev.components];
                              const current = components.findIndex((entry) => entry.name === item.name);
                              if (current < 0) return prev;
                              components[current] = { ...components[current], conditionScore: event.target.value };
                              return { ...prev, components };
                            })}
                          >
                            <MenuItem value="">—</MenuItem>
                            {SCORE_GUIDE.map((row) => (
                              <MenuItem key={row.score} value={row.score}>
                                {row.score} — {row.rating}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          label="Comments"
                          value={component.comments}
                          fullWidth
                          size="small"
                          onChange={(event) => setForm((prev) => {
                            const components = [...prev.components];
                            const current = components.findIndex((entry) => entry.name === item.name);
                            if (current < 0) return prev;
                            components[current] = { ...components[current], comments: event.target.value };
                            return { ...prev, components };
                          })}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            ))}
          </Stack>

          <SectionTitle step={3}>Defects</SectionTitle>
          <Typography variant="body2" sx={{ mb: 1.5, color: '#555' }}>
            Optional. Add a defect only when one is found.
          </Typography>
          <Button
            variant="outlined"
            fullWidth={isMobile}
            sx={{
              mb: 2,
              color: '#1b5e20',
              borderColor: '#66bb6a',
              backgroundColor: 'rgba(232, 245, 233, 0.55)',
              minHeight: 48,
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
              '&:hover': {
                borderColor: '#2e7d32',
                backgroundColor: 'rgba(200, 230, 201, 0.65)'
              }
            }}
            onClick={() => setForm((prev) => ({
              ...prev,
              defects: [...prev.defects, {
                ...emptyDefect(),
                inspectorName: prev.inspectorName,
                inspectionDate: todayIsoDate(),
                defectNumber: `${prev.assetNumber}_${String(prev.defects.length + 1).padStart(3, '0')}`
              }]
            }))}
          >
            Add defect
          </Button>

          {form.defects.map((defect, index) => (
            <Accordion
              key={index}
              defaultExpanded={index === form.defects.length - 1}
              disableGutters
              elevation={0}
              sx={{
                mb: 1.5,
                border: '1px solid rgba(46, 125, 50, 0.18)',
                borderRadius: '12px !important',
                backgroundColor: '#fff',
                boxShadow: '0 3px 12px rgba(20, 40, 20, 0.05)',
                overflow: 'hidden',
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  {defect.defectNumber || `Defect ${index + 1}`}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1.5}>
                  <TwoCol>
                    <TextField
                      label="Inspector's name"
                      value={defect.inspectorName}
                      onChange={(event) => setForm((prev) => {
                        const defects = [...prev.defects];
                        defects[index] = { ...defects[index], inspectorName: event.target.value };
                        return { ...prev, defects };
                      })}
                      fullWidth
                    />
                    <TextField
                      label="Inspection date"
                      type="date"
                      value={defect.inspectionDate}
                      InputLabelProps={{ shrink: true }}
                      onChange={(event) => setForm((prev) => {
                        const defects = [...prev.defects];
                        defects[index] = { ...defects[index], inspectionDate: event.target.value };
                        return { ...prev, defects };
                      })}
                      fullWidth
                    />
                  </TwoCol>
                  <TextField
                    label="Defect number"
                    value={defect.defectNumber}
                    onChange={(event) => setForm((prev) => {
                      const defects = [...prev.defects];
                      const defectNumber = event.target.value;
                      const safeDefectNumber = defectNumber.replace(/[<>:"/\\|?*]/g, '_').trim();
                      defects[index] = {
                        ...defects[index],
                        defectNumber,
                        photos: (defects[index].photos || []).map((photo, photoIndex) => ({
                          ...photo,
                          name: `${safeDefectNumber || 'defect'}_${photoIndex + 1}.jpg`
                        }))
                      };
                      return { ...prev, defects };
                    })}
                    fullWidth
                  />
                  <TwoCol>
                    <SelectOrCustom
                      key={`area-${dialogOpen}-${editingId || 'new'}-${index}`}
                      label="Area of asset"
                      value={defect.area}
                      options={COMPONENTS}
                      onChange={(next) => setForm((prev) => {
                        const defects = [...prev.defects];
                        defects[index] = { ...defects[index], area: next };
                        return { ...prev, defects };
                      })}
                    />
                    <SelectOrCustom
                      key={`type-${dialogOpen}-${editingId || 'new'}-${index}`}
                      label="Defect type code"
                      value={defect.typeCode}
                      options={DEFECT_TYPE_CODES}
                      onChange={(next) => setForm((prev) => {
                        const defects = [...prev.defects];
                        defects[index] = { ...defects[index], typeCode: next };
                        return { ...prev, defects };
                      })}
                    />
                  </TwoCol>
                  <FormControl fullWidth>
                    <InputLabel id={`defect-severity-${index}`}>Severity</InputLabel>
                    <Select
                      labelId={`defect-severity-${index}`}
                      label="Severity"
                      value={defect.severity}
                      onChange={(event) => setForm((prev) => {
                        const defects = [...prev.defects];
                        defects[index] = { ...defects[index], severity: event.target.value };
                        return { ...prev, defects };
                      })}
                    >
                      <MenuItem value="">Select severity</MenuItem>
                      {SEVERITY_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Details / description"
                    value={defect.details}
                    onChange={(event) => setForm((prev) => {
                      const defects = [...prev.defects];
                      defects[index] = { ...defects[index], details: event.target.value };
                      return { ...prev, defects };
                    })}
                    fullWidth
                    multiline
                    minRows={2}
                  />
                  <TextField
                    label="Repair / remediation recommendation"
                    value={defect.recommendation}
                    onChange={(event) => setForm((prev) => {
                      const defects = [...prev.defects];
                      defects[index] = { ...defects[index], recommendation: event.target.value };
                      return { ...prev, defects };
                    })}
                    fullWidth
                    multiline
                    minRows={2}
                  />
                  <TextField
                    label="Estimated repair cost"
                    value={defect.cost}
                    onChange={(event) => setForm((prev) => {
                      const defects = [...prev.defects];
                      defects[index] = { ...defects[index], cost: withDollar(event.target.value) };
                      return { ...prev, defects };
                    })}
                    fullWidth
                  />
                  <PhotoPicker
                    touch={touchCamera}
                    buttonLabel={touchCamera ? 'Take defect photo' : 'Upload defect photos'}
                    helper={`Named ${defect.defectNumber || 'defect'}_#.jpg`}
                    photos={defect.photos}
                    onAdd={(dataUrls) => setForm((prev) => {
                      const defects = [...prev.defects];
                      const safeDefectNumber = String(defects[index].defectNumber || '').replace(/[<>:"/\\|?*]/g, '_').trim() || 'defect';
                      const photos = [...(defects[index].photos || []), ...dataUrls.map((dataUrl) => ({ dataUrl }))];
                      defects[index] = {
                        ...defects[index],
                        photos: photos.map((photo, photoIndex) => ({
                          ...photo,
                          name: `${safeDefectNumber}_${photoIndex + 1}.jpg`
                        }))
                      };
                      return { ...prev, defects };
                    })}
                    onRemove={(photoIndex) => setForm((prev) => {
                      const defects = [...prev.defects];
                      const safeDefectNumber = String(defects[index].defectNumber || '').replace(/[<>:"/\\|?*]/g, '_').trim() || 'defect';
                      defects[index] = {
                        ...defects[index],
                        photos: defects[index].photos
                          .filter((_, current) => current !== photoIndex)
                          .map((photo, current) => ({
                            ...photo,
                            name: `${safeDefectNumber}_${current + 1}.jpg`
                          }))
                      };
                      return { ...prev, defects };
                    })}
                  />
                  <Button
                    color="error"
                    fullWidth={isMobile}
                    sx={{ minHeight: 44, textTransform: 'none' }}
                    onClick={() => setForm((prev) => ({
                      ...prev,
                      defects: prev.defects.filter((_, current) => current !== index)
                    }))}
                  >
                    Remove defect
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}

          {message && dialogOpen && (
            <Typography sx={{ mt: 2, color: '#c62828' }}>{message}</Typography>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            background: 'linear-gradient(180deg, #ffffff 0%, #f3f7f3 100%)',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: 1,
            px: { xs: 2, sm: 3 },
            py: 1.85,
            borderTop: '1px solid rgba(46, 125, 50, 0.14)'
          }}
        >
          <Button
            fullWidth={isMobile}
            onClick={() => setDialogOpen(false)}
            sx={{
              minHeight: 48,
              textTransform: 'none',
              fontWeight: 600,
              color: '#333',
              border: '1px solid #d0d0d0',
              backgroundColor: '#fff',
              '&:hover': { backgroundColor: '#f5f5f5', borderColor: '#bbb' }
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth={isMobile}
            variant="contained"
            disabled={saving || !canSave}
            onClick={saveReport}
            sx={{
              minHeight: 48,
              fontSize: 16,
              textTransform: 'none',
              fontWeight: 700,
              px: 3,
              background: 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)',
              boxShadow: '0 6px 16px rgba(46, 125, 50, 0.35)',
              '&:hover': {
                background: 'linear-gradient(135deg, #388e3c 0%, #1b5e20 100%)',
                boxShadow: '0 8px 20px rgba(46, 125, 50, 0.4)'
              }
            }}
          >
            {saving ? 'Saving…' : 'Save report'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminInspection;
